import type WebApiClient from "../../api/WebApiClient.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type GenMutation } from "@/graphql.ts";
import {
  LOAD_MUTATION_FAILURE,
  LOAD_MUTATION_REQUEST,
  LOAD_MUTATION_SUCCESS,
  RESET_MUTATION,
} from "@/redux/action-types.ts";

/**
 * State definition
 */
export interface IMutationState {
  mutation?: GenMutation;
  isLoading: boolean;
}

const initialState: IMutationState = {
  isLoading: false,
  mutation: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [RESET_MUTATION]: (state: IMutationState): IMutationState => {
    return { ...state, mutation: null };
  },
  [LOAD_MUTATION_REQUEST]: (state: IMutationState, _payload: GenMutation): IMutationState => {
    return { ...state, isLoading: true };
  },
  [LOAD_MUTATION_SUCCESS]: (state: IMutationState, payload: GenMutation): IMutationState => {
    return { ...state, isLoading: false, mutation: payload };
  },
  [LOAD_MUTATION_FAILURE]: (state: IMutationState, _payload): IMutationState => {
    return { ...state, isLoading: false, mutation: null };
  },
});

/**
 * Action creators
 */
const pendingPromisesLookup: Record<string, Promise<GenMutation>> = {}; // map by mutationAndRefGenome

// This id is going to be composed by mutationId and refGenomeId
// because simultaneous requests can be done for the same mutation with a different genome reference
let lastFetchedMutationId = "";

export function shouldLoadGenMutation(mutationId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<GenMutation> => {
    const state: IAppState = getState();
    const refGenomeId: number = state.auth.session && state.auth.session.params.referenceGenome;

    // reference genome is required !
    if (!refGenomeId) {
      // probably null, or whatever is currently in there (transition state)
      return Promise.resolve(state.mutation.mutation);
    }

    const mutationAndRefGenome = `${mutationId}-${refGenomeId}`;

    // return already existing promise corresponding to the same request
    if (pendingPromisesLookup[mutationAndRefGenome]) {
      return pendingPromisesLookup[mutationAndRefGenome];
    }

    // Fetch the GenMutation only if GenMutation is either absent or doesn't match the mutation ID
    // or if the reference genome setting has changed
    if (
      state.mutation.mutation &&
      state.mutation.mutation.id === mutationId &&
      state.mutation.mutation.refVariant.refGenome.id === refGenomeId
    ) {
      return Promise.resolve(state.mutation.mutation);
    }

    lastFetchedMutationId = mutationAndRefGenome;
    dispatch({ type: LOAD_MUTATION_REQUEST, payload: { mutationId, refGenomeId } });

    pendingPromisesLookup[mutationAndRefGenome] = webApiClient
      .fetchGenMutation(mutationId, refGenomeId)
      .then(
        (response: GenMutation) => {
          delete pendingPromisesLookup[mutationAndRefGenome];

          const RefGenome = mutationAndRefGenome.split("-")[1];
          const lastSplitMutationId = lastFetchedMutationId.split("-");

          // This test is a bit light but as it is used only to prevent re-rending we can consider it acceptable
          // if the genome reference is different, it is ok
          if (lastSplitMutationId[0] === String(response.id) || lastSplitMutationId[1] !== RefGenome) {
            dispatch({
              type: LOAD_MUTATION_SUCCESS,
              payload: response,
            });

            return response;
          }

          return state.mutation.mutation;
        },
        (rejected) => {
          delete pendingPromisesLookup[mutationAndRefGenome];
          throw rejected;
        },
      )
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_MUTATION_FAILURE,
          alertTitle: "Fetching of Variant Failed",
        }),
      );
    return pendingPromisesLookup[mutationAndRefGenome];
  };
}
